import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Alert,
  Button,
  InputGroup,
  Form,
  Badge,
  Spinner,
  ListGroup,
  Modal,
  Tabs,
  Tab
} from 'react-bootstrap';
import { 
  FaSearch,
  FaDownload,
  FaEye,
  FaFilePdf,
  FaFileWord,
  FaFileExcel,
  FaFileImage,
  FaGraduationCap,
  FaCalendarAlt,
  FaBook,
  FaFilter,
  FaSortAmountDown,
  FaShareAlt,
  FaBookOpen,
  FaUniversity,
  FaInfoCircle,
  FaExternalLinkAlt,
  FaCalendar,
  FaClock,
  FaChartLine,
  FaTag,
  FaList,
  FaFileAlt
} from 'react-icons/fa';
import axios from 'axios';
import { BASE_URL, UPLOAD_BASE_URL } from '../../services/api';

const ProspectusView = () => {
  const [prospectus, setProspectus] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProgram, setSelectedProgram] = useState('all');
  const [sortBy, setSortBy] = useState('recent');
  const [selectedProspectus, setSelectedProspectus] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [activeYear, setActiveYear] = useState('all');
  
  // Correct URL definitions using imported constants
  const API_URL = `${BASE_URL}/prospectus.php`;
  
  // Helper functions
  const getFileIcon = (filePath) => {
    if (!filePath) return <FaFileAlt className="text-secondary" />;
    const extension = filePath.split('.').pop().toLowerCase();
    switch (extension) {
      case 'pdf':
        return <FaFilePdf className="text-danger" />;
      case 'doc':
      case 'docx':
        return <FaFileWord className="text-primary" />;
      case 'xls':
      case 'xlsx':
        return <FaFileExcel className="text-success" />;
      case 'jpg':
      case 'jpeg':
      case 'png':
        return <FaFileImage className="text-warning" />;
      default:
        return <FaFileAlt className="text-secondary" />;
    }
  };
  
  const getFileType = (filePath) => {
    if (!filePath) return 'Unknown';
    const extension = filePath.split('.').pop().toUpperCase();
    return extension === 'DOCX' ? 'Word' : extension === 'XLSX' ? 'Excel' : extension;
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };
  
  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };
  
  const getFileUrl = (filePath) => {
    if (!filePath) return null;
    // Remove leading slash if present
    const cleanPath = filePath.startsWith('/') ? filePath.substring(1) : filePath;
    return `${UPLOAD_BASE_URL}/${cleanPath}`;
  };
  
  // Get filtered prospectus based on active filters
  const getFilteredProspectus = useCallback(() => {
    let filtered = [...prospectus];
    
    // Filter by academic year
    if (activeYear !== 'all') {
      filtered = filtered.filter(item => item.academic_year_id == activeYear);
    }
    
    // Filter by program
    if (selectedProgram !== 'all') {
      filtered = filtered.filter(item => item.program_name === selectedProgram);
    }
    
    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(item =>
        item.title.toLowerCase().includes(term) ||
        item.program_name.toLowerCase().includes(term) ||
        (item.description && item.description.toLowerCase().includes(term)) ||
        item.academic_year.toLowerCase().includes(term)
      );
    }
    
    // Sort data
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'recent':
          return new Date(b.uploaded_at) - new Date(a.uploaded_at);
        case 'oldest':
          return new Date(a.uploaded_at) - new Date(b.uploaded_at);
        case 'title':
          return a.title.localeCompare(b.title);
        case 'program':
          return a.program_name.localeCompare(b.program_name);
        default:
          return new Date(b.uploaded_at) - new Date(a.uploaded_at);
      }
    });
    
    return filtered;
  }, [prospectus, activeYear, selectedProgram, searchTerm, sortBy]);
  
  // Get document count for each year
  const getYearCounts = useCallback(() => {
    const counts = {
      all: prospectus.length
    };
    
    academicYears.forEach(year => {
      counts[year.id] = prospectus.filter(item => item.academic_year_id == year.id).length;
    });
    
    return counts;
  }, [prospectus, academicYears]);
  
  // Get statistics
  const getStatistics = () => {
    const filtered = getFilteredProspectus();
    const yearCounts = getYearCounts();
    
    return {
      total: filtered.length,
      years: academicYears.length,
      programs: new Set(prospectus.map(p => p.program_name)).size,
      activeYearCount: yearCounts[activeYear] || 0,
      recentYear: academicYears.length > 0 ? academicYears[0].academic_year : 'N/A'
    };
  };
  
  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      console.log('Fetching data from:', API_URL);
      
      // Fetch prospectus
      const response = await axios.get(API_URL, {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Prospectus response:', response.data);
      
      if (response.data.success && Array.isArray(response.data.data)) {
        // Process the data
        const processedData = response.data.data.map(item => ({
          ...item,
          id: parseInt(item.id),
          academic_year_id: parseInt(item.academic_year_id),
          file_url: getFileUrl(item.file_path)
        }));
        
        setProspectus(processedData);
      } else {
        setError(response.data.message || 'No prospectus available');
        setProspectus([]);
      }
      
      // Fetch academic years
      const yearsResponse = await axios.get(`${API_URL}?academic_years=1`);
      console.log('Years response:', yearsResponse.data);
      
      if (yearsResponse.data.success) {
        setAcademicYears(yearsResponse.data.data);
      }
      
      // Fetch programs
      const programsResponse = await axios.get(`${API_URL}?programs=1`);
      console.log('Programs response:', programsResponse.data);
      
      if (programsResponse.data.success) {
        setPrograms(programsResponse.data.data);
      }
      
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load prospectus. Please try again later.');
      setProspectus([]);
      setAcademicYears([]);
      setPrograms([]);
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  const handleDownload = (filePath) => {
    if (filePath) {
      const fileUrl = getFileUrl(filePath);
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = filePath.split('/').pop();
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };
  
  const handlePreview = (filePath) => {
    if (filePath) {
      const fileUrl = getFileUrl(filePath);
      window.open(fileUrl, '_blank');
    }
  };
  
  const handleShare = (item) => {
    if (navigator.share) {
      navigator.share({
        title: item.title,
        text: `Check out this prospectus: ${item.title} - ${item.program_name} (${item.academic_year})`,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };
  
  const handleViewDetails = (item) => {
    setSelectedProspectus(item);
    setShowDetailModal(true);
  };
  
  const clearFilters = () => {
    setSearchTerm('');
    setSelectedProgram('all');
    setActiveYear('all');
    setSortBy('recent');
  };
  
  const stats = getStatistics();
  const filteredProspectus = getFilteredProspectus();
  const yearCounts = getYearCounts();
  
  if (loading) {
    return (
      <Container className="py-5">
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <h4 className="mt-4" style={{ color: '#1a237e' }}>Loading Prospectus</h4>
          <p className="text-muted">Please wait while we load the academic prospectus...</p>
        </div>
      </Container>
    );
  }
  
  return (
    <div style={{ background: '#f8f9fa', minHeight: '100vh' }}>
      {/* Hero Section */}
      <div style={{
        background: 'linear-gradient(135deg, #1a237e 0%, #283593 100%)',
        padding: '80px 0 60px',
        color: 'white',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <Container>
          <Row className="align-items-center">
            <Col lg={7}>
              <Badge bg="light" text="dark" className="mb-3 px-3 py-2">
                <FaGraduationCap className="me-2" /> Academic Prospectus
              </Badge>
              <h1 className="display-5 fw-bold mb-4">
                Our <span style={{ color: '#64ffda' }}>Prospectus</span>
              </h1>
              <p className="lead mb-4">
                Browse through our comprehensive collection of academic prospectus 
                organized by academic years. Find detailed information about 
                courses, admission requirements, and program structures.
              </p>
              <div className="d-flex flex-wrap gap-4">
                <div>
                  <h2 className="mb-0">{stats.total}</h2>
                  <small className="text-white-50">Total Prospectus</small>
                </div>
                <div>
                  <h2 className="mb-0">{stats.years}</h2>
                  <small className="text-white-50">Academic Years</small>
                </div>
                <div>
                  <h2 className="mb-0">{stats.programs}</h2>
                  <small className="text-white-50">Programs</small>
                </div>
              </div>
            </Col>
            <Col lg={5} className="text-center">
              <div className="position-relative" style={{ height: '250px' }}>
                <div style={{
                  position: 'absolute',
                  top: '30px',
                  right: '30px',
                  background: 'rgba(255, 255, 255, 0.95)',
                  borderRadius: '15px',
                  padding: '20px',
                  width: '200px',
                  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
                  animation: 'float 6s ease-in-out infinite'
                }}>
                  <div style={{
                    fontSize: '32px',
                    color: '#1a237e',
                    marginBottom: '10px'
                  }}>
                    <FaCalendar />
                  </div>
                  <h5 className="mb-2">Academic Year</h5>
                  <p className="small mb-0">{stats.recentYear}</p>
                </div>
                
                <div style={{
                  position: 'absolute',
                  bottom: '50px',
                  right: '120px',
                  background: 'rgba(255, 255, 255, 0.95)',
                  borderRadius: '15px',
                  padding: '20px',
                  width: '180px',
                  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
                  animation: 'float 6s ease-in-out infinite 2s'
                }}>
                  <div style={{
                    fontSize: '32px',
                    color: '#1a237e',
                    marginBottom: '10px'
                  }}>
                    <FaBookOpen />
                  </div>
                  <h5 className="mb-2">Program Guides</h5>
                  <p className="small mb-0">Comprehensive outlines</p>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
        
        <style>{`
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-15px); }
          }
        `}</style>
      </div>
      
      {/* Search and Filter Section */}
      <Container>
        <Card className="border-0 shadow-lg" style={{
          borderRadius: '20px',
          marginTop: '-40px',
          position: 'relative',
          zIndex: 10
        }}>
          <Card.Body className="p-4">
            <Row className="g-3">
              <Col md={6}>
                <InputGroup>
                  <InputGroup.Text style={{
                    background: 'white',
                    borderRight: 'none',
                    borderTopLeftRadius: '15px',
                    borderBottomLeftRadius: '15px'
                  }}>
                    <FaSearch />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Search prospectus by title, program, or description..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{
                      borderLeft: 'none',
                      borderTopRightRadius: '15px',
                      borderBottomRightRadius: '15px',
                      padding: '15px'
                    }}
                  />
                </InputGroup>
              </Col>
              <Col md={3}>
                <Form.Select
                  value={selectedProgram}
                  onChange={(e) => setSelectedProgram(e.target.value)}
                >
                  <option value="all">All Programs</option>
                  {programs.map((program, index) => (
                    <option key={index} value={program.program_name}>
                      {program.program_name}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <option value="recent">Most Recent</option>
                  <option value="oldest">Oldest First</option>
                  <option value="title">Title A-Z</option>
                  <option value="program">Program A-Z</option>
                </Form.Select>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </Container>
      
      {/* Main Content */}
      <Container className="py-5">
        {error ? (
          <Alert variant="danger" className="border-0 shadow-sm">
            <div className="d-flex align-items-center">
              <FaInfoCircle className="me-3" size={24} />
              <div>
                <Alert.Heading>Unable to Load Prospectus</Alert.Heading>
                <p>{error}</p>
                <Button variant="primary" onClick={fetchData}>
                  Try Again
                </Button>
              </div>
            </div>
          </Alert>
        ) : prospectus.length === 0 ? (
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center py-5">
              <FaBook size={48} className="text-muted mb-3" />
              <h4 className="mb-3">No Prospectus Found</h4>
              <p className="text-muted mb-4">
                No prospectus documents are currently available.
              </p>
            </Card.Body>
          </Card>
        ) : (
          <>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h2 className="mb-0">
                  <FaCalendar className="me-2" />
                  Prospectus by Academic Year
                </h2>
                <p className="text-muted mb-0">
                  {filteredProspectus.length} document{filteredProspectus.length !== 1 ? 's' : ''} found
                </p>
              </div>
              <Button 
                variant="outline-primary" 
                size="sm"
                onClick={clearFilters}
              >
                Clear Filters
              </Button>
            </div>
            
            {/* Year Tabs */}
            <Card className="border-0 shadow-sm mb-4">
              <Card.Body className="p-0">
                <Tabs
                  activeKey={activeYear}
                  onSelect={(k) => setActiveYear(k)}
                  className="mb-0"
                  fill
                >
                  <Tab 
                    eventKey="all" 
                    title={
                      <div className="d-flex align-items-center justify-content-center">
                        <FaCalendarAlt className="me-2" />
                        All Years
                        <Badge bg="light" text="dark" className="ms-2">
                          {yearCounts.all}
                        </Badge>
                      </div>
                    }
                  />
                  
                  {academicYears.map(year => (
                    <Tab 
                      key={year.id} 
                      eventKey={year.id.toString()}
                      title={
                        <div className="d-flex align-items-center justify-content-center">
                          {year.academic_year}
                          <Badge bg="light" text="dark" className="ms-2">
                            {yearCounts[year.id] || 0}
                          </Badge>
                        </div>
                      }
                    />
                  ))}
                </Tabs>
              </Card.Body>
            </Card>
            
            {/* Year Summary */}
            <Card className="border-0 shadow-sm mb-4">
              <Card.Body>
                <Row className="align-items-center">
                  <Col md={8}>
                    <h5 className="mb-2">
                      {activeYear === 'all' ? (
                        <>
                          <FaCalendar className="me-2" />
                          All Academic Years
                        </>
                      ) : (
                        <>
                          <FaCalendar className="me-2" />
                          {academicYears.find(y => y.id.toString() === activeYear)?.academic_year || 'Unknown Year'}
                        </>
                      )}
                    </h5>
                    <p className="text-muted mb-0">
                      {activeYear === 'all' 
                        ? `Showing all ${filteredProspectus.length} prospectus from ${academicYears.length} academic years`
                        : `Showing ${filteredProspectus.length} prospectus for this academic year`
                      }
                    </p>
                  </Col>
                  <Col md={4} className="text-md-end">
                    <div className="d-flex flex-wrap gap-2 justify-content-md-end">
                      <Badge bg="info" className="px-3 py-2">
                        <FaBook className="me-1" />
                        {filteredProspectus.length} Documents
                      </Badge>
                      <Badge bg="success" className="px-3 py-2">
                        <FaUniversity className="me-1" />
                        {new Set(filteredProspectus.map(p => p.program_name)).size} Programs
                      </Badge>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
            
            {/* Prospectus Grid */}
            {filteredProspectus.length > 0 ? (
              <Row className="g-4 mb-5">
                {filteredProspectus.map((item) => (
                  <Col key={item.id} lg={4} md={6}>
                    <Card className="h-100 border-0 shadow-sm hover-shadow" 
                      style={{ transition: 'transform 0.3s, box-shadow 0.3s' }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-5px)';
                        e.currentTarget.style.boxShadow = '0 10px 20px rgba(0, 0, 0, 0.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '';
                      }}
                    >
                      <Card.Body className="p-4">
                        <div className="d-flex align-items-start mb-3">
                          <div style={{
                            width: '50px',
                            height: '50px',
                            borderRadius: '10px',
                            background: 'linear-gradient(135deg, #1976d2 0%, #0d47a1 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: '24px',
                            marginRight: '15px'
                          }}>
                            {getFileIcon(item.file_path)}
                          </div>
                          <div>
                            <Badge bg="light" text="dark" className="mb-2">
                              {getFileType(item.file_path)}
                            </Badge>
                            <h6 className="mb-1" style={{ color: '#1a237e' }}>
                              {item.title}
                            </h6>
                          </div>
                        </div>
                        
                        <div className="mb-3">
                          <div className="d-flex align-items-center mb-2">
                            <FaUniversity className="me-2" style={{ color: '#666' }} />
                            <strong className="me-2">Program:</strong>
                            <span>{item.program_name}</span>
                          </div>
                          <div className="d-flex align-items-center mb-2">
                            <FaClock className="me-2" style={{ color: '#666' }} />
                            <strong className="me-2">Uploaded:</strong>
                            <span>{formatDate(item.uploaded_at)}</span>
                          </div>
                          <div className="d-flex align-items-center">
                            <FaTag className="me-2" style={{ color: '#666' }} />
                            <strong className="me-2">Year:</strong>
                            <span>{item.academic_year}</span>
                          </div>
                        </div>
                        
                        {item.description && (
                          <div className="mb-3">
                            <p className="text-muted small" style={{ 
                              height: '60px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}>
                              {item.description}
                            </p>
                          </div>
                        )}
                        
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleViewDetails(item)}
                              className="me-2"
                            >
                              <FaEye className="me-1" />
                              Details
                            </Button>
                          </div>
                          <div className="d-flex gap-2">
                            <Button
                              variant="outline-success"
                              size="sm"
                              onClick={() => handlePreview(item.file_path)}
                              disabled={!item.file_path}
                            >
                              <FaExternalLinkAlt className="me-1" />
                              View
                            </Button>
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => handleDownload(item.file_path)}
                              disabled={!item.file_path}
                            >
                              <FaDownload className="me-1" />
                              Download
                            </Button>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            ) : (
              <Card className="border-0 shadow-sm mb-4">
                <Card.Body className="text-center py-5">
                  <FaBook size={48} className="text-muted mb-3" />
                  <h4 className="mb-3">No Matching Prospectus</h4>
                  <p className="text-muted mb-4">
                    No documents match your current filters. Try adjusting your search criteria.
                  </p>
                  <Button 
                    variant="outline-primary"
                    onClick={clearFilters}
                  >
                    Clear All Filters
                  </Button>
                </Card.Body>
              </Card>
            )}
            
            {/* Statistics Section */}
            <Card className="border-0 shadow-sm mb-4">
              <Card.Body>
                <h5 className="mb-4">
                  <FaChartLine className="me-2" />
                  Prospectus Statistics
                </h5>
                <Row>
                  <Col md={3} className="text-center mb-3">
                    <div className="p-3 bg-primary bg-opacity-10 rounded">
                      <h2 className="mb-0" style={{ color: '#1a237e' }}>{prospectus.length}</h2>
                      <small className="text-muted">Total Documents</small>
                    </div>
                  </Col>
                  
                  <Col md={3} className="text-center mb-3">
                    <div className="p-3 bg-success bg-opacity-10 rounded">
                      <h2 className="mb-0" style={{ color: '#2e7d32' }}>{academicYears.length}</h2>
                      <small className="text-muted">Academic Years</small>
                    </div>
                  </Col>
                  
                  <Col md={3} className="text-center mb-3">
                    <div className="p-3 bg-warning bg-opacity-10 rounded">
                      <h2 className="mb-0" style={{ color: '#f57c00' }}>{new Set(prospectus.map(p => p.program_name)).size}</h2>
                      <small className="text-muted">Programs</small>
                    </div>
                  </Col>
                  
                  <Col md={3} className="text-center mb-3">
                    <div className="p-3 bg-info bg-opacity-10 rounded">
                      <h2 className="mb-0" style={{ color: '#0288d1' }}>
                        {academicYears.length > 0 ? academicYears[0].academic_year : 'N/A'}
                      </h2>
                      <small className="text-muted">Latest Year</small>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
            
            {/* Quick Access by Program */}
            {programs.length > 0 && (
              <Card className="border-0 shadow-sm">
                <Card.Body>
                  <h5 className="mb-4">
                    <FaList className="me-2" />
                    Quick Access by Program
                  </h5>
                  <div className="d-flex flex-wrap gap-2">
                    {programs.map((program, index) => {
                      const programCount = prospectus.filter(p => p.program_name === program.program_name).length;
                      if (programCount === 0) return null;
                      
                      return (
                        <Button
                          key={index}
                          variant={selectedProgram === program.program_name ? 'primary' : 'outline-primary'}
                          size="sm"
                          onClick={() => setSelectedProgram(program.program_name)}
                          className="d-flex align-items-center"
                        >
                          <FaUniversity className="me-2" />
                          {program.program_name}
                          <Badge 
                            bg={selectedProgram === program.program_name ? 'light' : 'primary'} 
                            text={selectedProgram === program.program_name ? 'dark' : 'white'} 
                            className="ms-2"
                          >
                            {programCount}
                          </Badge>
                        </Button>
                      );
                    })}
                  </div>
                </Card.Body>
              </Card>
            )}
          </>
        )}
      </Container>
      
      {/* Detail Modal */}
      <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="lg" centered>
        {selectedProspectus && (
          <>
            <Modal.Header closeButton className="border-0 pb-0">
              <Modal.Title>
                <div className="d-flex align-items-center">
                  <div style={{
                    width: '70px',
                    height: '70px',
                    borderRadius: '15px',
                    background: 'linear-gradient(135deg, #1976d2 0%, #0d47a1 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '32px',
                    marginRight: '15px'
                  }}>
                    {getFileIcon(selectedProspectus.file_path)}
                  </div>
                  <div>
                    <h3 className="mb-1">{selectedProspectus.title}</h3>
                    <div className="d-flex align-items-center gap-3">
                      <Badge bg="info" className="px-3 py-2">
                        {getFileType(selectedProspectus.file_path)} Document
                      </Badge>
                      <small className="text-muted">
                        <FaCalendarAlt className="me-1" />
                        {formatDate(selectedProspectus.uploaded_at)}
                      </small>
                    </div>
                  </div>
                </div>
              </Modal.Title>
            </Modal.Header>
            
            <Modal.Body className="p-4">
              <Row>
                <Col md={8}>
                  <div className="mb-4">
                    <h5 className="mb-3">Document Information</h5>
                    <div className="p-3 bg-light rounded">
                      <Row>
                        <Col md={6} className="mb-3">
                          <strong className="d-block text-muted">Academic Year</strong>
                          <div className="d-flex align-items-center mt-2">
                            <FaCalendar className="me-2" />
                            <span>{selectedProspectus.academic_year}</span>
                          </div>
                        </Col>
                        <Col md={6} className="mb-3">
                          <strong className="d-block text-muted">Program</strong>
                          <div className="d-flex align-items-center mt-2">
                            <FaUniversity className="me-2" />
                            <span>{selectedProspectus.program_name}</span>
                          </div>
                        </Col>
                        <Col md={6} className="mb-3">
                          <strong className="d-block text-muted">File Type</strong>
                          <div className="d-flex align-items-center mt-2">
                            {getFileIcon(selectedProspectus.file_path)}
                            <span className="ms-2">{getFileType(selectedProspectus.file_path)}</span>
                          </div>
                        </Col>
                        <Col md={6} className="mb-3">
                          <strong className="d-block text-muted">Uploaded</strong>
                          <div className="d-flex align-items-center mt-2">
                            <FaClock className="me-2" />
                            <span>{formatDateTime(selectedProspectus.uploaded_at)}</span>
                          </div>
                        </Col>
                      </Row>
                    </div>
                  </div>
                  
                  {selectedProspectus.description && (
                    <div className="mb-4">
                      <h5 className="mb-3">Description</h5>
                      <div className="p-3 bg-light rounded">
                        <p className="mb-0">{selectedProspectus.description}</p>
                      </div>
                    </div>
                  )}
                </Col>
                
                <Col md={4}>
                  <Card className="border-0 bg-light">
                    <Card.Body className="p-3">
                      <h6 className="mb-3">Document Actions</h6>
                      <ListGroup variant="flush">
                        <ListGroup.Item className="bg-transparent border-0 px-0 py-2">
                          <Button
                            variant="primary"
                            className="w-100 mb-2"
                            onClick={() => handleDownload(selectedProspectus.file_path)}
                            disabled={!selectedProspectus.file_path}
                          >
                            <FaDownload className="me-2" />
                            Download Document
                          </Button>
                        </ListGroup.Item>
                        <ListGroup.Item className="bg-transparent border-0 px-0 py-2">
                          <Button
                            variant="outline-primary"
                            className="w-100 mb-2"
                            onClick={() => handlePreview(selectedProspectus.file_path)}
                            disabled={!selectedProspectus.file_path}
                          >
                            <FaExternalLinkAlt className="me-2" />
                            View in Browser
                          </Button>
                        </ListGroup.Item>
                        <ListGroup.Item className="bg-transparent border-0 px-0 py-2">
                          <Button
                            variant="outline-secondary"
                            className="w-100 mb-2"
                            onClick={() => handleShare(selectedProspectus)}
                          >
                            <FaShareAlt className="me-2" />
                            Share
                          </Button>
                        </ListGroup.Item>
                      </ListGroup>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Modal.Body>
            
            <Modal.Footer className="border-0">
              <Button 
                variant="secondary" 
                onClick={() => setShowDetailModal(false)}
              >
                Close
              </Button>
            </Modal.Footer>
          </>
        )}
      </Modal>
    </div>
  );
};

export default ProspectusView;